import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import { Dispatch } from '@/store'
import useMediaQuery from '@/hooks/use-media-query'
import { Heading, ParagraphRegular, Label } from '@/components/text'
import { FieldInput, FieldTextarea } from '@/components/form'
import { Button } from '@/components/button'
import { createModal, useModalize } from '@/components/modalize/mui'
import { Stack, Box } from '@/components/structure'
import { breakpoints, colors } from '@/styles'
import { useStopPadSuccessModal } from '@/features/Pad'

const FormContents = styled(Form)`
  display: contents;
`

type FeedbackItemProps = {
  value: string
  children: React.ReactNode
}

const FeedbackItem: React.FC<FeedbackItemProps> = ({ value, children, ...rest }) => (
  <Box gap="0 8px" align="center" as="label">
    <FieldInput name="feedback" type="radio" value={value} {...rest} />
    <Label fontSize="14px" as="span">
      {children}
    </Label>
  </Box>
)

type StopPadProps = {
  contractId?: String
}

const StopPadModal: React.FC<StopPadProps> = ({ contractId }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch<Dispatch>()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const padding = displayMobile ? 24 : 32
  const { show: showSuccessModal } = useStopPadSuccessModal()

  const handleSubmit = async () => {
    const { response } = await dispatch.contract.stopPadPayments({
      contractId
    })
    if (response.ok) {
      showSuccessModal()
      close()
    }
  }

  return (
    <Box ptop={padding + 20} pbottom={padding} pleft={padding} pright={padding}>
      <Stack gutter="32px">
        <Stack gutter="8px">
          <Box textAlign="center" justify="center">
            <Heading type={!displayMobile ? 'h4' : 'h5'}>
              {t(
                'r.pad_settings.edit.stop_pad.heading',
                'Are you sure you want to stop automatic rent payments (PAD)?'
              )}
            </Heading>
          </Box>
          <Box textAlign="center" justify="center" color={colors.grey56}>
            <ParagraphRegular>
              {t(
                'r.pad_settings.edit.stop_pad.text',
                'Please select the reason from the following options:'
              )}
            </ParagraphRegular>
          </Box>
        </Stack>
        <Formik initialValues={{}} onSubmit={handleSubmit}>
          {() => (
            <FormContents>
              <Stack gutter="16px" color={colors.grey64} width="100%">
                <FeedbackItem value="lease_ended">
                  {t('r.pad_settings.edit.stop_pad.lease_ended.option', 'The lease has ended')}
                </FeedbackItem>
                <FeedbackItem value="payment_change_tenant">
                  {t(
                    'r.pad_settings.edit.stop_pad.payment_change_tenant.option',
                    'I no longer want to pay my rent this way'
                  )}
                </FeedbackItem>
                <FeedbackItem value="payment_change_landlord">
                  {t(
                    'r.pad_settings.edit.stop_pad.payment_change_landlord.option',
                    'My landlord has requested a different payment method'
                  )}
                </FeedbackItem>
                <FeedbackItem value="payment_dispute">
                  {t(
                    'r.pad_settings.edit.stop_pad.payment_dispute.option',
                    'I have a payment dispute with my landlord'
                  )}
                </FeedbackItem>
                <FeedbackItem value="other">
                  {t('r.pad_settings.edit.stop_pad.other.option', 'Other.')}
                </FeedbackItem>
                <FieldTextarea
                  name="custom_feedback"
                  placeholder={t(
                    'r.pad_settings.edit.stop_pad.feedback.placeholder',
                    'Please describe the reason here.'
                  )}
                />
              </Stack>
              <Button type="submit">{t('common.submit', 'Submit')}</Button>
            </FormContents>
          )}
        </Formik>
      </Stack>
    </Box>
  )
}

const show = createModal(StopPadModal, { width: '492px' })

export default show
